import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import FormError from '../../../components/form-error/FormError';
import { formatNumber } from '../../../utils';
import { getFormActions } from '../../../utils/form-helpers';
import stripeFormConfig, { canSubmit } from '../../../payment/stripe-form-config';
import LoaderBar from '../../../components/loader-bar/LoaderBar';
import VisibleIf from '../../../components/VisibleIf';
import { trackEvent } from '../../../support-and-tracking/analytics-actions';
import EmailSuggestion from '../../../components/EmailSuggestion';
import { SectionTitle } from '../../../components/typography/Heading';
import RoundedButtonAction from '../../../components/button/rounded-button-action/RoundedButtonAction';
import styles from './stripe-form.module.less';
import Input2 from '../../../components/input/input-2/Input2';
import { isAnyRewardNeitherLotteryNorProductReward } from '../cart/cart-model';
import { useI18nTexts } from '../../i18n/useI18nTexts';
import {
    CARD_DETAILS_TEXT_KEY,
    EMAIL_ADDRESS_IS_USED_FOR_TEXT_KEY,
    ERROR_IN_EMAIL_ADDRESS_TEXT_KEY,
    HANDLING_PAYMENT_TEXT_KEY,
    isUserUsingNorwegianLanguageOnProject,
    NOK_TEXT_KEY,
    PAY_TEXT_KEY,
    YOUR_EMAIL_TEXT_KEY,
} from '../../i18n/project-i18n-model';
import { useProject } from '../../project-hooks/useProject';

const stripeFormActions = getFormActions(stripeFormConfig);

const getCardErrorFromStripeError = (project, error) => {
    if (isUserUsingNorwegianLanguageOnProject(project)) {
        if (error.type === 'validation_error') {
            switch (error.code) {
                case 'incomplete_number':
                    return 'Du må fylle ut kortnummeret';
                case 'incomplete_expiry':
                    return 'Du må fylle ut utløpsdato';
                case 'invalid_expiry_year_past':
                    return 'Det ser ut til at kortet har utløpt';
                case 'invalid_number':
                    return 'Du har fylt inn et ugyldig kortnummer';
                case 'incomplete_cvc':
                    return 'Du må fylle ut CVC';
                case 'incomplete_zip':
                    return 'Du må fylle ut postnummer';
                default:
                    return 'Det er et problem med informasjonen du har fylt ut';
            }
        } else if (error.type === 'invalid_request_error') {
            switch (error.code) {
                case 'card_declined':
                    return 'Kortet ditt ble avvist. Prøv et annet kort eller ta kontakt med banken din.';
                default:
                    return 'Kortet ble ikke akseptert';
            }
        }
        return 'Det oppstod et problem ved forsøk på å starte belastningen av kortet';
    } else {
        if (error.type === 'validation_error') {
            switch (error.code) {
                case 'incomplete_number':
                    return 'You must enter the card number';
                case 'incomplete_expiry':
                    return 'You must enter the expiration date';
                case 'invalid_expiry_year_past':
                    return 'It appears the card has expired';
                case 'invalid_number':
                    return 'You have entered an invalid card number';
                case 'incomplete_cvc':
                    return 'You must fill in the CVC';
                case 'incomplete_zip':
                    return 'You must enter your postal code';
                default:
                    return 'There is a problem with the information you have provided';
            }
        } else if (error.type === 'invalid_request_error') {
            switch (error.code) {
                case 'card_declined':
                    return 'Your card was declined. Try a different card or contact your bank';
                default:
                    return 'The card was not accepted.';
            }
        }
        return 'There was a problem charging your card';
    }
};

const StripeForm = ({ predefinedEmail = '', stripeForm, amountInKr, initiateCallback, rewards, isCart }) => {
    const dispatch = useDispatch();
    const project = useProject();
    const supportButton = useRef();
    useEffect(() => {
        stripeFormActions.setFormField('email', predefinedEmail);
    }, [predefinedEmail]);

    const stripe = useStripe();
    const elements = useElements();
    const texts = useI18nTexts([
        EMAIL_ADDRESS_IS_USED_FOR_TEXT_KEY,
        ERROR_IN_EMAIL_ADDRESS_TEXT_KEY,
        HANDLING_PAYMENT_TEXT_KEY,
        CARD_DETAILS_TEXT_KEY,
        YOUR_EMAIL_TEXT_KEY,
        NOK_TEXT_KEY,
        PAY_TEXT_KEY,
    ]);

    const onCardElementChange = (event) => {
        const card = event.error ? getCardErrorFromStripeError(project, event.error) : undefined;
        dispatch(
            stripeFormActions.setFormField('errors', {
                ...stripeForm.errors,
                card,
            })
        );
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();

        if (stripeForm.isPerformingPayment) {
            return;
        }

        trackEvent('support', 'checkout-pay', {
            method: 'Stripe',
            'project-id': project.id,
            amount: amountInKr,
            name: !!stripeForm.giverName,
            comment: !!stripeForm.comment,
            cart: isCart,
            reward: rewards?.length > 0,
        });

        dispatch(stripeFormActions.setFormField('isPerformingPayment', true));

        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: elements.getElement(CardElement),
        });

        if (error) {
            dispatch(stripeFormActions.setFormField('isPerformingPayment', false));
            dispatch(
                stripeFormActions.setFormField('errors', {
                    ...stripeForm.errors,
                    card: getCardErrorFromStripeError(project, error),
                })
            );
        } else if (canSubmit(stripeForm)) {
            initiateCallback({
                paymentMethod,
                email: stripeForm.email,
                stripe,
            });
        }
    };

    const { email, errors, isProcessingPayment } = stripeForm;

    const cardError = errors && errors.card ? errors.card : '';
    const paymentError = errors?.global ? errors.global : '';
    const emailText = `${texts[EMAIL_ADDRESS_IS_USED_FOR_TEXT_KEY]} ${
        isAnyRewardNeitherLotteryNorProductReward({ rewards })
            ? 'og for å gi den til innsamler for at du skal kunne kontaktes om belønningen din'
            : ''
    }`;

    return (
        <form onSubmit={handleSubmit}>
            {isProcessingPayment ? (
                <div className={styles.isPaying}>
                    <SectionTitle>{texts[HANDLING_PAYMENT_TEXT_KEY]}</SectionTitle>
                    <LoaderBar />
                </div>
            ) : (
                <>
                    <Input2
                        title={texts[CARD_DETAILS_TEXT_KEY]}
                        error={cardError}
                        inputCreator={(inputProps) => (
                            <CardElement
                                {...inputProps}
                                className={[inputProps.className, styles.stripeElement].join(' ')}
                                onChange={onCardElementChange}
                                hidePostalCode
                            />
                        )}
                    />

                    <Input2
                        wrapperClass={styles.emailInput}
                        title={texts[YOUR_EMAIL_TEXT_KEY]}
                        fieldDescription={emailText}
                        fieldName="email"
                        value={email}
                        error={errors?.email ? texts[ERROR_IN_EMAIL_ADDRESS_TEXT_KEY] : false}
                        type="email"
                        onChange={(e) => dispatch(stripeFormActions.setFormField('email', e.target.value.trim()))}
                        onBlur={(e) => dispatch(stripeFormActions.setFormField('email', e.target.value.trim(), true))}
                    />
                    <EmailSuggestion className={styles.emailSuggestion} email={email} />
                    <VisibleIf isVisible={!!paymentError}>
                        <FormError className={styles.error} error={paymentError} />
                    </VisibleIf>

                    <RoundedButtonAction
                        category="supportform"
                        action="stripe-pay"
                        label={amountInKr && amountInKr.toString()}
                        disabled={!canSubmit(stripeForm) || stripeForm.isPerformingPayment}
                        className={styles.button}
                        buttonRef={supportButton}
                    >{`${texts[PAY_TEXT_KEY]} ${formatNumber(amountInKr)} ${texts[NOK_TEXT_KEY]}`}</RoundedButtonAction>
                </>
            )}
        </form>
    );
};

StripeForm.propTypes = {
    predefinedEmail: PropTypes.string,
    stripeForm: PropTypes.object.isRequired,
    amountInKr: PropTypes.number,
    initiateCallback: PropTypes.func.isRequired,
    rewards: PropTypes.array.isRequired,
    isCart: PropTypes.bool,
};

export default StripeForm;
